import { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type {
  ActiveFilters,
  DateRangeFilter,
  DateRangeValue,
  Filter,
  FilterValue,
  MultiSelectFilter,
} from './filterTypes';
import { filterKey } from './filterTypes';

// ── popover helpers ───────────────────────────────────────────────────────────

function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return { open, setOpen, ref };
}

function PillButton({
  label, summary, isActive, editMode, onClick, onClear, onRemove, dragHandleProps,
}: {
  label: string;
  summary?: string;
  isActive: boolean;
  editMode: boolean;
  onClick: () => void;
  onClear: (e: React.MouseEvent) => void;
  onRemove: (e: React.MouseEvent) => void;
  dragHandleProps?: Record<string, unknown>;
}) {
  return (
    <button
      {...dragHandleProps}
      onClick={editMode ? undefined : onClick}
      className={[
        'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all select-none',
        editMode
          ? 'border-dashed border-muted-foreground/50 bg-muted/30 text-muted-foreground cursor-grab active:cursor-grabbing'
          : isActive
          ? 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/15'
          : 'border-border bg-background text-muted-foreground hover:text-foreground hover:bg-accent',
      ].join(' ')}
    >
      {editMode && <span className="text-[10px] opacity-40">⠿</span>}
      <span>{label}</span>
      {!editMode && isActive && summary && (
        <span className="text-xs font-semibold opacity-80 max-w-[100px] truncate">{summary}</span>
      )}
      {!editMode && isActive && (
        <span
          role="button"
          onClick={onClear}
          className="ml-0.5 flex items-center justify-center w-4 h-4 rounded-full hover:bg-primary/20 transition-colors text-[10px]"
        >✕</span>
      )}
      {!editMode && !isActive && <span className="text-[10px] opacity-50">▾</span>}
      {editMode && (
        <span
          role="button"
          onClick={onRemove}
          className="ml-0.5 flex items-center justify-center w-4 h-4 rounded-full bg-destructive/80 text-destructive-foreground hover:bg-destructive transition-colors text-[9px] font-bold"
          title="Hide filter"
        >✕</span>
      )}
    </button>
  );
}

function PopoverCard({ children, header }: { children: React.ReactNode; header: string }) {
  return (
    <div className="absolute top-full left-0 mt-2 z-50 bg-popover border border-border rounded-xl shadow-2xl overflow-hidden min-w-[230px]">
      <div className="px-4 py-3 border-b border-border bg-muted/40">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{header}</p>
      </div>
      {children}
    </div>
  );
}

function PopoverFooter({ onApply, onClear }: { onApply: () => void; onClear: () => void }) {
  return (
    <div className="px-3 py-2.5 border-t border-border bg-muted/20 flex gap-2">
      <button
        onClick={onClear}
        className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-accent transition-colors text-muted-foreground"
      >Clear</button>
      <button
        onClick={onApply}
        className="flex-1 text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity font-medium"
      >Apply</button>
    </div>
  );
}

// ── multiselect pill ──────────────────────────────────────────────────────────

/**
 * Values of a lazy filter, asked for when its dropdown opens and again as the
 * user types (searched on the server, first 200 matches). Inactive for filters
 * whose values came with the dashboard.
 */
function useRemoteOptions(filter: MultiSelectFilter, open: boolean, search: string) {
  const [state, setState] = useState({ values: [] as string[], matched: 0, loading: false, failed: false });
  useEffect(() => {
    if (!open || !filter.options_lazy) return;
    let alive = true;
    setState(s => ({ ...s, loading: true, failed: false }));
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ filter_id: filter.id, limit: '200' });
      if (search.trim()) params.set('q', search.trim());
      fetch(`/api/dashboard/filter-options?${params.toString()}`)
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then((d: { values?: string[]; matched?: number }) => {
          if (alive) setState({ values: d.values ?? [], matched: d.matched ?? 0, loading: false, failed: false });
        })
        .catch(() => { if (alive) setState({ values: [], matched: 0, loading: false, failed: true }); });
    }, search ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [open, search, filter.id, filter.options_lazy]);
  return state;
}

function MultiSelectPill({ filter, value, editMode, onApply, onClear, onRemove, dragHandleProps }: {
  filter: MultiSelectFilter;
  value: string[] | undefined;
  editMode: boolean;
  onApply: (v: string[]) => void;
  onClear: () => void;
  onRemove: () => void;
  dragHandleProps?: Record<string, unknown>;
}) {
  const { open, setOpen, ref } = usePopover();
  const [draft, setDraft] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const remote = useRemoteOptions(filter, open, search);

  const openPopover = () => { setDraft(value ?? []); setSearch(''); setOpen(true); };
  const toggle = (opt: string) => setDraft(d => d.includes(opt) ? d.filter(x => x !== opt) : [...d, opt]);
  const listed = filter.options_lazy
    ? remote.values
    : filter.options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  // What is ticked stays on screen even when the search or the first page of
  // values does not include it, so it can always be unticked.
  const ticked = draft.filter(v => !listed.includes(v));
  const filtered = [...ticked, ...listed];
  const isActive = (value?.length ?? 0) > 0;
  const summary = value?.length === 1 ? value[0] : `${value?.length}`;

  return (
    <div className="relative" ref={ref}>
      <PillButton
        label={filter.label} summary={isActive ? summary : undefined}
        isActive={isActive} editMode={editMode}
        onClick={openPopover}
        onClear={e => { e.stopPropagation(); onClear(); }}
        onRemove={e => { e.stopPropagation(); onRemove(); }}
        dragHandleProps={dragHandleProps}
      />
      {open && !editMode && (
        <PopoverCard header={filter.label}>
          <div className="px-3 pt-3 pb-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-background text-sm">
              <span className="text-muted-foreground text-xs">⌕</span>
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search…"
                className="flex-1 bg-transparent outline-none text-xs placeholder:text-muted-foreground" />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto px-1 pb-1">
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground px-3 py-4 text-center">
                {remote.loading ? 'Loading values…' : remote.failed ? 'Values could not be loaded.' : 'No results'}
              </p>
            )}
            {filtered.map(opt => (
              <label key={opt} onClick={() => toggle(opt)}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-accent cursor-pointer transition-colors">
                <span className={['w-4 h-4 rounded flex items-center justify-center border transition-colors flex-shrink-0',
                  draft.includes(opt) ? 'bg-primary border-primary text-primary-foreground' : 'border-border'].join(' ')}>
                  {draft.includes(opt) && <span className="text-[9px] font-bold">✓</span>}
                </span>
                <span className="truncate text-xs">{opt}</span>
              </label>
            ))}
          </div>
          {filter.options_lazy && remote.matched > remote.values.length && (
            <div className="px-3 py-1.5 text-xs text-muted-foreground border-t border-border">
              Showing {remote.values.length.toLocaleString()} of {remote.matched.toLocaleString()} — type to narrow
            </div>
          )}
          {draft.length > 0 && (
            <div className="px-3 py-1.5 text-xs text-muted-foreground border-t border-border bg-muted/10">{draft.length} selected</div>
          )}
          <PopoverFooter
            onApply={() => { onApply(draft); setOpen(false); }}
            onClear={() => { setDraft([]); onClear(); setOpen(false); }}
          />
        </PopoverCard>
      )}
    </div>
  );
}

// ── date range pill ───────────────────────────────────────────────────────────

const DATE_PRESETS = [
  { label: 'Last 7 days',  getRange: () => { const t = new Date(), f = new Date(); f.setDate(t.getDate() - 7);  return { from: toISO(f), to: toISO(t) }; } },
  { label: 'Last 30 days', getRange: () => { const t = new Date(), f = new Date(); f.setDate(t.getDate() - 30); return { from: toISO(f), to: toISO(t) }; } },
  { label: 'Last 90 days', getRange: () => { const t = new Date(), f = new Date(); f.setDate(t.getDate() - 90); return { from: toISO(f), to: toISO(t) }; } },
  { label: 'This year',    getRange: () => { const t = new Date(); return { from: `${t.getFullYear()}-01-01`, to: toISO(t) }; } },
  { label: 'Last year',    getRange: () => { const y = new Date().getFullYear() - 1; return { from: `${y}-01-01`, to: `${y}-12-31` }; } },
];

function toISO(d: Date) { return d.toISOString().split('T')[0]; }
function isoToDMY(s: string) { const [y, m, d] = s.split('-'); return (d && m && y) ? `${d}/${m}/${y}` : s; }

function DateRangePill({ filter, value, editMode, onApply, onClear, onRemove, dragHandleProps }: {
  filter: DateRangeFilter;
  value: DateRangeValue | undefined;
  editMode: boolean;
  onApply: (v: DateRangeValue) => void;
  onClear: () => void;
  onRemove: () => void;
  dragHandleProps?: Record<string, unknown>;
}) {
  const { open, setOpen, ref } = usePopover();
  const [draft, setDraft] = useState({ from: '', to: '' });

  const openPopover = () => { setDraft({ from: value?.from ?? '', to: value?.to ?? '' }); setOpen(true); };
  const isActive = !!(value?.from || value?.to);
  const summary = value?.from && value?.to
    ? `${isoToDMY(value.from)} – ${isoToDMY(value.to)}`
    : value?.from ? `From ${isoToDMY(value.from)}` : value?.to ? `To ${isoToDMY(value.to)}` : undefined;

  return (
    <div className="relative" ref={ref}>
      <PillButton
        label={filter.label} summary={summary}
        isActive={isActive} editMode={editMode}
        onClick={openPopover}
        onClear={e => { e.stopPropagation(); onClear(); }}
        onRemove={e => { e.stopPropagation(); onRemove(); }}
        dragHandleProps={dragHandleProps}
      />
      {open && !editMode && (
        <PopoverCard header={filter.label}>
          <div className="px-3 pt-3 pb-2 flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-1.5">
              {DATE_PRESETS.map(p => (
                <button key={p.label} onClick={() => { const v = p.getRange(); setDraft(v); onApply(v); setOpen(false); }}
                  className="text-xs px-2 py-1.5 rounded-lg border border-border hover:bg-accent hover:border-primary/30 transition-colors text-muted-foreground hover:text-foreground">
                  {p.label}
                </button>
              ))}
            </div>
            <div className="border-t border-border pt-2 flex flex-col gap-2">
              {/* A native date input renders in the BROWSER's locale, and the
                  `lang` attribute here never changed that — Chrome ignores it.
                  So an en-US browser showed 08/09/2026 for a date this app and
                  every ERP report around it call 09/08/2026, and a reader had
                  no way to tell which one they had picked. The picker stays
                  native (keyboard, mobile, min/max all come free) and the
                  chosen date is echoed unambiguously underneath it. */}
              {(['from', 'to'] as const).map(k => (
                <div key={k} className="flex flex-col gap-1">
                  <label className="text-xs text-muted-foreground font-medium" htmlFor={`daterange-${filter.id}-${k}`}>
                    {k === 'from' ? 'From' : 'To'}
                  </label>
                  <input id={`daterange-${filter.id}-${k}`} type="date" value={draft[k]}
                    onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))}
                    className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary w-full" />
                  <span className="text-[11px] text-muted-foreground tabular-nums" dir="ltr">
                    {draft[k] ? isoToDMY(draft[k]) : 'dd/mm/yyyy'}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <PopoverFooter
            onApply={() => { if (draft.from || draft.to) { onApply(draft); setOpen(false); } }}
            onClear={() => { setDraft({ from: '', to: '' }); onClear(); setOpen(false); }}
          />
        </PopoverCard>
      )}
    </div>
  );
}

// ── sortable pill wrapper ─────────────────────────────────────────────────────

function SortablePill({ filter, active, editMode, onApply, onClear, onRemove }: {
  filter: Filter;
  active: ActiveFilters;
  editMode: boolean;
  onApply: (key: string, val: FilterValue | null) => void;
  onClear: (key: string) => void;
  onRemove: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: filter.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  const key = filterKey(filter);
  const dragHandleProps = editMode ? { ...attributes, ...listeners } : {};

  const sharedProps = {
    editMode,
    onRemove: () => onRemove(filter.id),
    dragHandleProps,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {filter.type === 'daterange' && (
        <DateRangePill filter={filter} value={active[key] as DateRangeValue | undefined}
          onApply={v => onApply(key, v)} onClear={() => onClear(key)} {...sharedProps} />
      )}
      {filter.type === 'multiselect' && (
        <MultiSelectPill filter={filter} value={active[key] as string[] | undefined}
          onApply={v => onApply(key, v)} onClear={() => onClear(key)} {...sharedProps} />
      )}
    </div>
  );
}

// ── FilterPanel ───────────────────────────────────────────────────────────────

export default function FilterPanel({
  filters,
  catalog,
  active,
  editMode,
  onApply,
  onClearAll,
  onRemoveFilter,
  onReorder,
  hasActive,
}: {
  filters: Filter[];
  catalog: Filter[];
  active: ActiveFilters;
  editMode: boolean;
  onApply: (key: string, val: FilterValue | null) => void;
  onClearAll: () => void;
  onRemoveFilter: (id: string) => void;
  onReorder: (orderedIds: string[]) => void;
  hasActive: boolean;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [showHidden, setShowHidden] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const hiddenFilters = catalog.filter(f => !filters.find(v => v.id === f.id));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active: dragActive, over } = event;
    if (over && dragActive.id !== over.id) {
      const oldIndex = filters.findIndex(f => f.id === dragActive.id);
      const newIndex = filters.findIndex(f => f.id === over.id);
      const reordered = arrayMove(filters, oldIndex, newIndex);
      onReorder(reordered.map(f => f.id));
    }
  };

  if (filters.length === 0 && !editMode) return null;

  // Thirty-two chips is a wall, not a control. Only the dimensions a person
  // slices the business by are shown; the rest — keys, codes, the accountant's
  // account columns — sit behind one button. Nothing is removed: an ACTIVE
  // secondary filter is always visible (a filter you cannot see is a number you
  // cannot explain), and edit mode still shows everything so the layout can be
  // managed. In edit mode the order is the admin's, so no splitting there.
  const isSecondary = (f: Filter) =>
    f.tier === 'secondary' && !active[f.id];
  const visible = editMode || showMore
    ? filters
    : filters.filter(f => !isSecondary(f));
  const moreCount = filters.length - visible.length;

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-muted-foreground mr-1">Filters</span>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visible.map(f => f.id)} strategy={horizontalListSortingStrategy}>
            {visible.map(f => (
              <SortablePill
                key={f.id}
                filter={f}
                active={active}
                editMode={editMode}
                onApply={onApply}
                onClear={key => onApply(key, null)}
                onRemove={onRemoveFilter}
              />
            ))}
          </SortableContext>
        </DndContext>

        {!editMode && moreCount > 0 && (
          <button
            onClick={() => setShowMore(true)}
            className="text-xs text-muted-foreground hover:text-foreground rounded-full border border-dashed px-3 py-1.5 transition-colors"
          >
            + {moreCount} more filters
          </button>
        )}
        {!editMode && showMore && (
          <button
            onClick={() => setShowMore(false)}
            className="text-xs text-muted-foreground hover:text-foreground rounded-full border border-dashed px-3 py-1.5 transition-colors"
          >
            Show fewer
          </button>
        )}

        {!editMode && hasActive && (
          <button onClick={onClearAll} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors ml-1">
            Reset all
          </button>
        )}
      </div>

      {/* Hidden filters restore UI — only in editMode */}
      {editMode && hiddenFilters.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowHidden(h => !h)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <span>{showHidden ? '▾' : '▸'}</span>
            <span>{hiddenFilters.length} hidden filter{hiddenFilters.length > 1 ? 's' : ''}</span>
          </button>
          {showHidden && (
            <div className="flex flex-wrap gap-2 mt-2 pl-1">
              {hiddenFilters.map(f => (
                <button
                  key={f.id}
                  onClick={() => onRemoveFilter(`__restore__${f.id}`)}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border border-dashed border-muted-foreground/40 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                >
                  <span>+ {f.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
